import { useState, useCallback } from "react";
import { X, Printer, Eye, Pencil, GitCompare, ChevronRight, Save } from "lucide-react";
import type { CvDocument, CvDocumentCreate, SectionKey } from "features/cv-processor/api/CvProcessorApi";
import { getDocument, getEmployeeDocumentAsAdmin, createDocument, createEmployeeDocumentAsAdmin } from "features/cv-processor/api/CvProcessorApi";
import {
  CvPreview, buildPrintHtml, openPrintWindow,
  SECTION_KEYS, SECTION_LABELS, SECTION_PLACEHOLDERS, getDocSectionText, sectionToApiPayload,
  ScoreBadge,
  WorkExperienceEditor, EducationEditor, ProjectsEditor, SkillsEditor,
} from "features/cv-processor/CvProcessorPage";
import { ComparisonModal } from "features/cv-processor/EnhancementsPage";
import SaveAsNewCvModal from "features/cv-processor/ui/SaveAsNewCvModal";

interface Props {
  enhancedData: CvDocument;
  jobTitle: string;
  /** Job description text used for the original-vs-enhanced Compare view — pass
   * whatever field the caller's job shape uses (job_summary, description, etc.),
   * already resolved to a plain string so this component stays job-shape-agnostic. */
  jobDescription?: string | null;
  isAdmin: boolean;
  cvId?: number | null;
  /** Set when isAdmin and admin ran this enhancement on behalf of a Technical
   * Manager — required for "Save as new CV" to land in that employee's own
   * collection instead of the admin's. */
  employeeCode?: string | null;
  onClose: () => void;
}

/** Shared "View Enhanced CV" modal — used identically by the LinkedIn Email
 * Alerts and TheirStack (Other Jobs) pipelines. Do not fork this per-pipeline;
 * both call sites should look and behave exactly the same. */
export default function EnhancedCvModal({ enhancedData, jobTitle, jobDescription, isAdmin, cvId, employeeCode, onClose }: Props) {
  const [activeView, setActiveView] = useState<"edit" | "preview">("preview");
  const [activeSection, setActiveSection] = useState<SectionKey>("headline");
  const [localContent, setLocalContent] = useState<Record<SectionKey, string>>(
    () => SECTION_KEYS.reduce((acc, key) => {
      acc[key] = getDocSectionText(enhancedData, key);
      return acc;
    }, {} as Record<SectionKey, string>),
  );
  const [showComparison, setShowComparison] = useState(false);
  const [originalDoc, setOriginalDoc] = useState<CvDocument | null>(null);
  const [loadingOriginal, setLoadingOriginal] = useState(false);
  const [compareError, setCompareError] = useState<string | null>(null);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);

  const previewDoc: CvDocument = { ...enhancedData, ...(localContent as any) };

  const sectionScores = enhancedData.last_section_scores ?? null;
  const activeSectionScore = sectionScores?.[activeSection]?.total_score ?? null;
  const activeSectionFix = sectionScores?.[activeSection]?.top_priority_fix ?? null;
  const sectionHasContent = (key: SectionKey) => !!(localContent[key] || "").trim();
  const currentContent = localContent[activeSection] ?? "";

  const handleContentChange = useCallback(
    (value: string) => setLocalContent((prev) => ({ ...prev, [activeSection]: value })),
    [activeSection],
  );

  function handlePrint() {
    openPrintWindow(buildPrintHtml(previewDoc));
  }

  async function handleSaveAsNew(title: string, categoryId: number | null) {
    if (isAdmin && !employeeCode) {
      throw new Error("Could not determine which Technical Manager owns this CV. Please reselect them and try again.");
    }
    const payload: CvDocumentCreate = {
      title,
      category_id: categoryId,
      job_description: jobDescription ?? "",
      headline: localContent.headline,
      summary: localContent.summary,
      work_experience: sectionToApiPayload("work_experience", localContent.work_experience),
      skills: sectionToApiPayload("skills", localContent.skills),
      education: sectionToApiPayload("education", localContent.education),
      projects: sectionToApiPayload("projects", localContent.projects),
      header_info: enhancedData.header_info,
    };
    if (isAdmin && employeeCode) {
      await createEmployeeDocumentAsAdmin({ ...payload, employee_code: employeeCode });
    } else {
      await createDocument(payload);
    }
    setShowSaveModal(false);
    setSaveMessage("Saved as a new CV — find it under My CVs.");
  }

  async function handleCompare() {
    setCompareError(null);
    if (!originalDoc && cvId) {
      setLoadingOriginal(true);
      try {
        const doc = isAdmin
          ? await getEmployeeDocumentAsAdmin(cvId)
          : await getDocument(cvId);
        setOriginalDoc(doc);
      } catch {
        setLoadingOriginal(false);
        setCompareError("Could not load original CV. Please try again.");
        return;
      }
      setLoadingOriginal(false);
    }
    setShowComparison(true);
  }

  return (
    <div
      className="fixed inset-0 z-[999999] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-4xl max-h-[92vh] overflow-hidden bg-surface border border-border rounded-2xl flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h2 className="text-sm font-semibold text-foreground font-inter">Enhanced CV Preview</h2>
              {enhancedData.holistic_review?.overall_score != null && (
                <ScoreBadge score={enhancedData.holistic_review.overall_score} />
              )}
            </div>
            <p className="text-xs text-muted-foreground font-inter mt-0.5 truncate">Enhanced for: {jobTitle}</p>
          </div>
          <div className="flex items-center gap-2 shrink-0 ml-4">
            <button
              onClick={() => setShowSaveModal(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-surface-muted hover:bg-surface-muted border border-border text-foreground text-xs font-inter font-medium rounded-lg transition-colors"
            >
              <Save className="w-3.5 h-3.5" />
              Save as Regular CV
            </button>
            <button
              onClick={handlePrint}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-primary hover:bg-primary/90 text-primary-foreground text-xs font-inter font-medium rounded-lg transition-colors"
            >
              <Printer className="w-3.5 h-3.5" />
              Print / Save PDF
            </button>
            <button
              onClick={onClose}
              className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-surface-muted rounded-lg transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
        {saveMessage && (
          <div className="px-6 py-2 border-b border-border bg-emerald-500/10 shrink-0">
            <p className="text-xs text-emerald-400 font-inter">{saveMessage}</p>
          </div>
        )}

        {/* Tab bar */}
        <div className="flex items-center gap-3 px-6 py-3 border-b border-border shrink-0">
          <div className="flex items-center gap-1 p-1 bg-surface-muted rounded-xl w-fit border border-border">
            <button
              onClick={() => setActiveView("edit")}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium font-inter rounded-lg transition-colors ${
                activeView === "edit" ? "bg-surface text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <Pencil className="w-3.5 h-3.5" />
              Edit
            </button>
            <button
              onClick={() => setActiveView("preview")}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium font-inter rounded-lg transition-colors ${
                activeView === "preview" ? "bg-surface text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <Eye className="w-3.5 h-3.5" />
              Preview
            </button>
          </div>
          {cvId && (
            <div className="flex items-center gap-2">
              <button
                onClick={handleCompare}
                disabled={loadingOriginal}
                className="inline-flex items-center gap-1 text-xs font-inter text-muted-foreground hover:text-primary disabled:opacity-50 transition-colors"
              >
                <GitCompare className="w-3 h-3" />
                {loadingOriginal ? "Loading…" : "Compare"}
              </button>
              {compareError && (
                <span className="text-[11px] text-red-400 font-inter">{compareError}</span>
              )}
            </div>
          )}
        </div>

        {/* Body — scrollable */}
        <div className="flex-1 overflow-y-auto min-h-0 px-6 py-4">
          {activeView === "preview" ? (
            <CvPreview doc={previewDoc} />
          ) : (
            <div className="flex gap-6">
              {/* Section sidebar */}
              <div className="w-48 shrink-0 space-y-1">
                {SECTION_KEYS.map((key) => {
                  const isActive = activeSection === key;
                  const sectionScore = sectionScores?.[key]?.total_score ?? null;
                  return (
                    <button
                      key={key}
                      onClick={() => setActiveSection(key)}
                      className={`w-full flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-inter transition-colors text-left ${
                        isActive
                          ? "bg-primary/10 text-primary border border-primary/30"
                          : "text-muted-foreground hover:text-foreground hover:bg-surface-muted border border-transparent"
                      }`}
                    >
                      <span className="flex-1 truncate">{SECTION_LABELS[key]}</span>
                      {sectionScore !== null ? (
                        <ScoreBadge score={sectionScore} />
                      ) : sectionHasContent(key) && !isActive ? (
                        <ChevronRight className="w-3.5 h-3.5 shrink-0 opacity-40" />
                      ) : null}
                    </button>
                  );
                })}
              </div>

              {/* Section editor */}
              <div className="flex-1 min-w-0">
                <div className="rounded-2xl border border-border bg-surface/50 overflow-hidden">
                  <div className="px-4 py-3 border-b border-border flex items-center justify-between">
                    <h2 className="text-sm font-semibold text-foreground font-inter">
                      {SECTION_LABELS[activeSection]}
                    </h2>
                    {activeSectionScore !== null && (
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground font-inter">ATS</span>
                        <ScoreBadge score={activeSectionScore} />
                      </div>
                    )}
                  </div>

                  {activeSectionFix && activeSectionFix !== "none" && (
                    <div className="px-4 py-2.5 border-b border-border bg-surface-muted">
                      <p className="text-xs text-muted-foreground font-inter">
                        <span className="text-muted-foreground">Top fix: </span>
                        {activeSectionFix}
                      </p>
                    </div>
                  )}

                  {activeSection === "work_experience" ? (
                    <WorkExperienceEditor key="work_experience" value={currentContent} onChange={handleContentChange} onBlur={() => {}} />
                  ) : activeSection === "education" ? (
                    <EducationEditor key="education" value={currentContent} onChange={handleContentChange} onBlur={() => {}} />
                  ) : activeSection === "projects" ? (
                    <ProjectsEditor key="projects" value={currentContent} onChange={handleContentChange} onBlur={() => {}} />
                  ) : activeSection === "skills" ? (
                    <SkillsEditor key="skills" value={currentContent} onChange={handleContentChange} onBlur={() => {}} />
                  ) : (
                    <textarea
                      key={activeSection}
                      value={currentContent}
                      onChange={(e) => handleContentChange(e.target.value)}
                      placeholder={SECTION_PLACEHOLDERS[activeSection]}
                      rows={activeSection === "headline" ? 3 : 12}
                      className="w-full px-4 py-3 bg-transparent text-sm text-foreground font-inter placeholder:text-muted-foreground focus:outline-none resize-none"
                    />
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {showComparison && originalDoc && (
        <ComparisonModal
          originalDoc={originalDoc}
          enhancedDoc={previewDoc}
          jobDescription={jobDescription ?? ""}
          onClose={() => setShowComparison(false)}
        />
      )}
      {showSaveModal && (
        <SaveAsNewCvModal
          defaultTitle={`${jobTitle} — Tailored CV`}
          onSave={handleSaveAsNew}
          onClose={() => setShowSaveModal(false)}
        />
      )}
    </div>
  );
}
